import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Footer.module.scss";
import EMPBranding from "../components/EMPBranding/EMPBranding";
import { Typography, Box, Link as MuiLink } from "@mui/material";
import { usePermission } from "../hooks/usePermission";
import { IS_FORD_BRAND, IS_USA_MARKET } from "../../utils/EmpUtil";
import { getDealershipMarket } from "../../utils/usFieldSupport";

// Size, color and underline live in .upcomingChangesLink, which must outrank the generic
// footer link rule; only the brand font is set here.
const getUpcomingChangesFont = (brand) =>
  IS_FORD_BRAND(brand)
    ? '"Ford F-1", Arial, sans-serif'
    : '"Lincoln Proxima Nova", Arial, sans-serif';

const Footer = (props) => {
  const { t } = useTranslation();
  const hasHelpAndSupportLink = usePermission("helpAndSupportLink");
  const { simplified } = props;
  // US footers drop the left-side logo and portal tag and stay pinned to the bottom of the
  // screen; Canada keeps the original footer at the end of the page content.
  const hideBranding =
    simplified || IS_USA_MARKET(getDealershipMarket(props.user));
  const pinned = hideBranding;
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);

  // A fixed footer leaves the page flow, so a spacer of the same height keeps the end of the
  // page visible above it. The height changes when the links wrap on narrow screens.
  useLayoutEffect(() => {
    const footer = footerRef.current;
    if (!pinned || !footer) {
      return undefined;
    }

    const updateHeight = () => setFooterHeight(footer.offsetHeight);
    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      globalThis.addEventListener("resize", updateHeight);
      return () => globalThis.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(footer);
    return () => observer.disconnect();
  }, [pinned]);
  const showUpcomingChanges =
    (simplified || props.showUpcomingChanges) && !props.hideUpcomingChanges;

  return (
    <>
      {pinned && (
        <Box
          aria-hidden="true"
          data-testid="appFooterSpacer"
          sx={{ height: `${footerHeight}px` }}
        />
      )}
      <Box
        component="footer"
        ref={footerRef}
        sx={{
          ...(pinned && {
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            mx: "auto",
            maxWidth: "1440px",
            zIndex: 1000,
            backgroundColor: "#FFFFFF",
          }),
          py: 8,
          px: { xs: 4, sm: 4, md: 4, lg: 14 },
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: {
            xs: hideBranding ? "flex-end" : "flex-start",
            lg: hideBranding ? "flex-end" : "space-between",
          },
          alignItems: hideBranding
            ? { xs: "flex-end", lg: "center" }
            : undefined,
          width: "100%",
        }}
        data-testid="appFooter"
        className={`${styles.footerBox} ${
          hideBranding ? styles.footerAlignEnd : ""
        }`}
      >
        {!hideBranding && (
          <div>
            <EMPBranding
              showLogo={props.showBrandingLogo}
              brand={props.user?.brand}
              width="80px"
            />
          </div>
        )}
        <div
          className={`${styles.helpContainer} ${
            hideBranding ? styles.alignEnd : ""
          }`}
        >
          <ul>
            {showUpcomingChanges && (
              <li>
                <MuiLink
                  component={Link}
                  to="/ecommerce-releases"
                  underline="always"
                  className={styles.upcomingChangesLink}
                  sx={{ fontFamily: getUpcomingChangesFont(props.user?.brand) }}
                  data-testid="footer-upcoming-changes"
                >
                  {t("Footer.upcomingChanges", {
                    defaultValue: "Upcoming Changes to eCommerce",
                  })}
                </MuiLink>
              </li>
            )}
            {hasHelpAndSupportLink && !simplified && (
              <li>
                <Link to="/help">
                  <Typography
                    variant="body2"
                    sx={{ mb: 0, color: "#000000", cursor: "pointer" }}
                  >
                    {props.helpSupportTitle}
                  </Typography>
                </Link>
              </li>
            )}
          </ul>
        </div>
      </Box>
    </>
  );
};

Footer.propTypes = {
  hideUpcomingChanges: PropTypes.bool,
  helpSupportTitle: PropTypes.string,
  showBrandingLogo: PropTypes.bool,
  showUpcomingChanges: PropTypes.bool,
  simplified: PropTypes.bool,
  user: PropTypes.object,
};

Footer.defaultProps = {
  hideUpcomingChanges: false,
  helpSupportTitle: "",
  showBrandingLogo: true,
  showUpcomingChanges: false,
  simplified: false,
  user: undefined,
};

export default Footer;
